// src/components/resources/tools/generators/AdMockup/LinkedInAd.tsx
'use client'

import {
  ArrowPathIcon,
  HandThumbUpIcon,
  ChatBubbleOvalLeftIcon,
  PaperAirplaneIcon,
  GlobeAmericasIcon,
  EllipsisHorizontalIcon,
} from '@heroicons/react/24/outline'
import {
  HandThumbUpIcon as HandThumbUpSolidIcon,
  CheckBadgeIcon as CheckBadgeSolidIcon,
} from '@heroicons/react/24/solid'
import { clsx } from 'clsx'
import { Avatar } from './Avatar'
import { CreativeSlot } from './CreativeSlot'
import { formatCount } from './ad-format'
import type { AdState } from './types'

/**
 * LinkedIn single-image sponsored ad. LinkedIn marks ads "Promoted" (not
 * "Sponsored"), uses its own reaction row (Like / Comment / Repost / Send), and
 * renders the headline + CTA in a link bar beneath the creative — close to the
 * Facebook link ad but with LinkedIn chrome and the brand blue (#0A66C2).
 */
export function LinkedInAd({ state, creativeImage, avatarImage }: { state: AdState; creativeImage: string | null; avatarImage: string | null }): JSX.Element {
  const dark = state.theme === 'dark'
  return (
    <div className={clsx('overflow-hidden rounded-xl border font-sans shadow-md', dark ? 'border-slate-700 bg-slate-900 text-slate-100' : 'border-slate-200 bg-white text-slate-900')}>
      {/* header */}
      <div className="flex items-start gap-2.5 px-4 pt-3">
        <Avatar name={state.pageName} image={avatarImage} size={48} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1">
            <span className="truncate text-[14px] font-semibold">{state.pageName}</span>
            {state.verified && <CheckBadgeSolidIcon className="h-4 w-4 flex-shrink-0 text-[#0A66C2]" aria-label="Verified" />}
          </div>
          <div className={clsx('flex items-center gap-1 text-[12px]', dark ? 'text-slate-400' : 'text-slate-500')}>
            <span>Promoted</span>
            <span aria-hidden>·</span>
            <GlobeAmericasIcon className="h-3.5 w-3.5 flex-shrink-0" />
          </div>
        </div>
        <EllipsisHorizontalIcon className={clsx('h-5 w-5 flex-shrink-0', dark ? 'text-slate-400' : 'text-slate-500')} />
      </div>

      {/* intro text */}
      {state.primaryText.trim() && <p className="whitespace-pre-wrap px-4 py-3 text-sm leading-snug">{state.primaryText}</p>}

      {/* creative (1.91:1) */}
      <CreativeSlot image={creativeImage} alt={`${state.pageName} ad creative`} ratio={1 / 1.91} />

      {/* link bar: headline + CTA button */}
      <div className={clsx('flex items-center justify-between gap-3 px-4 py-3', dark ? 'bg-slate-800' : 'bg-slate-100')}>
        <div className="min-w-0">
          <div className="truncate text-[14px] font-semibold leading-tight">{state.headline}</div>
          <div className={clsx('truncate text-xs', dark ? 'text-slate-400' : 'text-slate-500')}>{state.displayUrl}</div>
        </div>
        <span className="flex-shrink-0 rounded-full border border-[#0A66C2] px-3.5 py-1 text-[13px] font-semibold text-[#0A66C2]">
          {state.cta}
        </span>
      </div>

      {/* reaction count */}
      <div className={clsx('flex items-center justify-between px-4 py-2 text-xs', dark ? 'text-slate-400' : 'text-slate-500')}>
        <span className="flex items-center gap-1">
          <span className="flex h-4 w-4 items-center justify-center rounded-full bg-[#0A66C2]"><HandThumbUpSolidIcon className="h-2.5 w-2.5 text-white" /></span>
          {formatCount(state.likes)}
        </span>
        <span>{formatCount(state.comments)} comments</span>
      </div>

      {/* action bar */}
      <div className={clsx('mx-2 flex items-center justify-around border-t py-1.5 text-sm font-medium', dark ? 'border-slate-700 text-slate-300' : 'border-slate-200 text-slate-600')}>
        <span className="flex items-center gap-1.5"><HandThumbUpIcon className="h-[18px] w-[18px]" /> Like</span>
        <span className="flex items-center gap-1.5"><ChatBubbleOvalLeftIcon className="h-[18px] w-[18px]" /> Comment</span>
        <span className="flex items-center gap-1.5"><ArrowPathIcon className="h-[18px] w-[18px]" /> Repost</span>
        <span className="flex items-center gap-1.5"><PaperAirplaneIcon className="h-[18px] w-[18px] -rotate-45" /> Send</span>
      </div>
    </div>
  )
}
